import { SerializedData, Store } from "bernie-core";
import { observable } from "mobx";
import { Logger } from "bernie-logger";
import { NewBlossomComponent } from "./NewBlossomComponent";
import { CompositionStore } from "stores/CompositionStore";
import { FlexModuleModelStore } from "stores/FlexModuleModelStore";

export class BlossomComponentTreeStore extends Store {
  @observable
  public tree: NewBlossomComponent[];

  @observable
  public currentScene: string | null;

  private treeHydrated: boolean;

  public constructor(state: SerializedData = {}, logger: Logger) {
    super(state, logger);
    this.tree = state.tree || [];
    this.currentScene = state.currentScene || null;
    this.treeHydrated = false;
  }

  /**
   * sets BlossomComponentTree with provided value
   * !!!IMPORTANT!!! will not work as expected if passed a tree object created from serialized state
   * run hydrateTree to fully rebuild a serialized tree
   * @param tree
   */
  public setTree = (tree: NewBlossomComponent[]) => {
    this.tree = tree;
    this.treeHydrated = true;
  };

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
    this.treeHydrated = false; // full hydration completed upon external call to hydrateTree()
  }

  /**
   * if necessary, hydrates serialized BlossomComponents in tree, adding methods and wiring to FlexModuleModelStore
   * @param flexModuleModelStore
   */
  public hydrateTree = (flexModuleModelStore: FlexModuleModelStore) => {
    if (this.treeHydrated) {
      return;
    }

    this.tree = this.hydrateBlossomComponents(this.tree, flexModuleModelStore);
    this.treeHydrated = true;
  };

  /**
   * takes a collection of serialized BlossomComponents and returns hydrated equivalent, iterating through all decendents
   * @param components
   * @param flexModuleModelStore
   */
  private hydrateBlossomComponents = (
    components: NewBlossomComponent[],
    flexModuleModelStore: FlexModuleModelStore
  ): NewBlossomComponent[] => {
    return components.map((component) => {
      const children = component.children
        ? this.hydrateBlossomComponents(component.children, flexModuleModelStore)
        : [];

      return new NewBlossomComponent({
        componentData: {
          id: component.id,
          name: component.name,
          fmId: component.fmId,
          fmTitleId: component.fmTitleId,
          view: component.view,
          scene: component.scene,
          children,
        },
        flexModuleModelStore,
        blossomComponentTreeStore: this,
      });
    });
  };

  /**
   * Sets currentScene and triggers corresponding data fetch in CompositionStore
   * @param newScene
   * @param compositionStore
   */
  public changeScene = async (newScene: string | null, compositionStore: CompositionStore) => {
    this.currentScene = newScene;

    if (newScene === null) {
      return;
    }

    return compositionStore.changeScene(newScene);
  };

  /**
   * returns first BlossomComponent in tree with matching fmId, undefined if not found
   * @returns NewBlossomComponent | undefined
   * @memberof BlossomComponentTree
   */
  public findBlossomComponentByFmId = (fmId?: number): NewBlossomComponent | undefined =>
    this.findBlossomComponent(this.tree, (component: NewBlossomComponent) => component.fmId === fmId);

  /**
   * returns first match in provided NewBlossomComponent[] collection which satisfies the provided test, iterating downward through all child components
   * @param {NewBlossomComponent[]} components
   * @param {function} test
   * @returns NewBlossomComponent | undefined
   * @private
   * @memberof BlossomComponentTree
   */
  private findBlossomComponent = (
    components: NewBlossomComponent[],
    test: (component: NewBlossomComponent) => boolean
  ): NewBlossomComponent | undefined => {
    let result: NewBlossomComponent | undefined;

    for (const component of components) {
      if (test(component)) {
        result = component;
        break;
      }

      if (component.hasChildren()) {
        result = this.findBlossomComponent(component.children!, test);

        if (result) {
          break;
        }
      }
    }

    return result;
  };

  /**
   * removes data that will be re-constructed client-side
   * @returns SerializedData
   */
  public toJSON(): SerializedData {
    return super.toJSON("treeHydrated");
  }
}
